import { create } from "zustand";
import { fetchUnanalyzedGames, type UnanalyzedGame } from "./analysis.api";

interface AnalysisDataState {
  ownerUserId: string | null;
  unanalyzed: UnanalyzedGame[] | null;
  isLoading: boolean;
  setOwner: (userId: string | null) => void;
  invalidateGames: () => void;
  loadUnanalyzed: () => Promise<void>;
  refreshUnanalyzed: () => Promise<void>;
}

let inFlightRequest: Promise<void> | null = null;
let inFlightRequestKind: "load" | "refresh" | null = null;
let queuedRefreshRequest: Promise<void> | null = null;
let ownerRevision = 0;

const requestUnanalyzed = (set: (state: Partial<AnalysisDataState>) => void, kind: "load" | "refresh") => {
  if (inFlightRequest) return inFlightRequest;

  const revision = ownerRevision;
  const request = fetchUnanalyzedGames()
    .then((unanalyzed) => {
      if (revision === ownerRevision) set({ unanalyzed, isLoading: false });
    })
    .catch((error: unknown) => {
      if (revision === ownerRevision) set({ isLoading: false });
      throw error;
    })
    .finally(() => {
      if (inFlightRequest === request) {
        inFlightRequest = null;
        inFlightRequestKind = null;
      }
    });

  inFlightRequest = request;
  inFlightRequestKind = kind;
  return request;
};

export const useAnalysisDataStore = create<AnalysisDataState>((set, get) => ({
  ownerUserId: null,
  unanalyzed: null,
  isLoading: true,
  invalidateGames: () => {
    ownerRevision += 1;
    inFlightRequest = null;
    inFlightRequestKind = null;
    queuedRefreshRequest = null;
    set({ unanalyzed: null, isLoading: false });
  },
  setOwner: (ownerUserId) => {
    if (get().ownerUserId === ownerUserId) return;
    ownerRevision += 1;
    inFlightRequest = null;
    inFlightRequestKind = null;
    queuedRefreshRequest = null;
    set({ ownerUserId, unanalyzed: null, isLoading: ownerUserId !== null });
  },
  loadUnanalyzed: async () => {
    if (get().unanalyzed !== null) return;
    return requestUnanalyzed(set, "load");
  },
  refreshUnanalyzed: async () => {
    if (!inFlightRequest || inFlightRequestKind === "refresh") {
      return requestUnanalyzed(set, "refresh");
    }
    if (queuedRefreshRequest) return queuedRefreshRequest;

    const revision = ownerRevision;
    const queued = inFlightRequest
      .catch(() => undefined)
      .then(() => (revision === ownerRevision ? requestUnanalyzed(set, "refresh") : undefined))
      .finally(() => {
        if (queuedRefreshRequest === queued) queuedRefreshRequest = null;
      });
    queuedRefreshRequest = queued;
    return queued;
  },
}));
